/**
 * Purchase Order Management Page
 * 
 * CRUD operations for purchase order management with workflow tracking
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Alert,
  CircularProgress,
  Grid,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
  GetApp as GetAppIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { PurchaseService } from '../services/purchaseService';
import { usePermissions } from '../hooks/usePermissions';
import {
  PurchaseOrder,
  PurchaseOrderStatus,
  PurchaseOrderListParams,
  PurchaseOrderListResponse,
} from '../types/purchase';
import { formatCurrency, formatDate } from '../utils/formatters';

const statusSteps = [
  PurchaseOrderStatus.DRAFT,
  PurchaseOrderStatus.SENT,
  PurchaseOrderStatus.ACKNOWLEDGED,
  PurchaseOrderStatus.RECEIVED,
  PurchaseOrderStatus.BILLED,
];

const PurchaseOrderManagementMUI: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Pagination and filtering
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<PurchaseOrderListParams>({});
  
  // UI state
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<PurchaseOrder | null>(null);

  useEffect(() => {
    loadOrders();
  }, [page, rowsPerPage, searchQuery, filters]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: PurchaseOrderListParams = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery || undefined,
        ...filters,
      };
      
      const response: PurchaseOrderListResponse = await PurchaseService.getPurchaseOrders(params);
      // Ensure orders is always an array - use purchase_orders as primary field
      const ordersData = response.purchase_orders || [];
      setOrders(Array.isArray(ordersData) ? ordersData : []);
      setTotalCount(response.total || 0);
    } catch (err: any) {
      console.error('Failed to load purchase orders:', err);
      setError(err.message || 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectOrder = (orderId: string) => {
    setSelectedOrders(prev => 
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAllOrders = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedOrders((orders || []).map(order => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, orderId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrderId(orderId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrderId(null);
  };

  const handleViewOrder = (orderId: string) => {
    navigate(`/admin/purchase/orders/${orderId}`);
    handleMenuClose();
  };

  const handleEditOrder = (orderId: string) => {
    navigate(`/admin/purchase/orders/${orderId}/edit`);
    handleMenuClose();
  };

  const handleDeleteOrder = (order: PurchaseOrder) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;
    
    try {
      await PurchaseService.deletePurchaseOrder(orderToDelete.id);
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      loadOrders();
    } catch (err: any) {
      console.error('Failed to delete purchase order:', err);
      setError(err.message || 'Failed to delete purchase order');
    }
  };

  const handleSendOrder = async (orderId: string) => {
    try {
      await PurchaseService.sendPurchaseOrder(orderId);
      loadOrders();
      handleMenuClose();
    } catch (err: any) {
      console.error('Failed to send purchase order:', err);
      setError(err.message || 'Failed to send purchase order');
    }
  };

  const handleAcknowledgeOrder = async (orderId: string) => {
    try {
      await PurchaseService.acknowledgePurchaseOrder(orderId);
      loadOrders();
      handleMenuClose();
    } catch (err: any) {
      console.error('Failed to acknowledge purchase order:', err);
      setError(err.message || 'Failed to acknowledge purchase order');
    }
  };

  const handleReceiveGoods = (orderId: string) => {
    navigate(`/admin/purchase/orders/${orderId}/receive`);
    handleMenuClose();
  };

  const handleConvertToBill = async (orderId: string) => {
    try {
      const bill = await PurchaseService.convertToBill(orderId);
      navigate(`/admin/purchase/bills/${bill.id}`);
      handleMenuClose();
    } catch (err: any) {
      console.error('Failed to convert to bill:', err);
      setError(err.message || 'Failed to convert to bill');
    }
  };

  const handlePrintOrder = async (orderId: string) => {
    try {
      const pdfBlob = await PurchaseService.generatePurchaseOrderPDF(orderId);
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
      handleMenuClose();
    } catch (err: any) {
      console.error('Failed to generate PDF:', err);
      setError(err.message || 'Failed to generate PDF');
    }
  };

  const getStatusColor = (status: PurchaseOrderStatus) => {
    switch (status) {
      case PurchaseOrderStatus.DRAFT:
        return 'default';
      case PurchaseOrderStatus.SENT:
        return 'info';
      case PurchaseOrderStatus.ACKNOWLEDGED:
        return 'primary';
      case PurchaseOrderStatus.PARTIAL_RECEIVED:
        return 'warning';
      case PurchaseOrderStatus.RECEIVED:
        return 'success';
      case PurchaseOrderStatus.BILLED:
        return 'success';
      case PurchaseOrderStatus.CLOSED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: PurchaseOrderStatus): React.ReactElement | undefined => {
    switch (status) {
      case PurchaseOrderStatus.SENT:
        return <SendIcon fontSize="small" />;
      case PurchaseOrderStatus.ACKNOWLEDGED:
        return <CheckCircleIcon fontSize="small" />;
      case PurchaseOrderStatus.RECEIVED:
        return <LocalShippingIcon fontSize="small" />;
      case PurchaseOrderStatus.BILLED:
        return <ReceiptIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  const getStatusStep = (status: PurchaseOrderStatus) => {
    return statusSteps.indexOf(status);
  };

  const getReceiveProgress = (order: PurchaseOrder) => {
    const totalQuantity = order.items.reduce((sum, item) => sum + item.quantity, 0);
    const receivedQuantity = order.items.reduce((sum, item) => sum + item.receivedQuantity, 0);
    return totalQuantity > 0 ? (receivedQuantity / totalQuantity) * 100 : 0;
  };

  const canPerformAction = (order: PurchaseOrder, action: string) => {
    switch (action) {
      case 'edit':
        return order.status === PurchaseOrderStatus.DRAFT;
      case 'send':
        return order.status === PurchaseOrderStatus.DRAFT;
      case 'acknowledge':
        return order.status === PurchaseOrderStatus.SENT;
      case 'receive':
        return order.status === PurchaseOrderStatus.ACKNOWLEDGED || order.status === PurchaseOrderStatus.PARTIAL_RECEIVED;
      case 'convert':
        return order.status === PurchaseOrderStatus.RECEIVED;
      case 'delete':
        return order.status === PurchaseOrderStatus.DRAFT;
      default:
        return false;
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadOrders}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Purchase Orders
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage purchase orders and track delivery status
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={() => {
              // Export implementation
              console.log('Exporting purchase orders...');
            }}
          >
            Export
          </Button>
          {hasPermission('purchase_orders.create') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/purchase/orders/create')}
            >
              New Purchase Order
            </Button>
          )}
        </Box>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by PO number, supplier name..."
                value={searchQuery}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => setFilterDialogOpen(true)}
                >
                  Filters
                </Button>
                {selectedOrders.length > 0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      // Bulk operations implementation
                      console.log('Bulk operations:', selectedOrders);
                    }}
                  >
                    Actions ({selectedOrders.length})
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedOrders.length > 0 && selectedOrders.length < (orders || []).length}
                    checked={(orders || []).length > 0 && selectedOrders.length === (orders || []).length}
                    onChange={handleSelectAllOrders}
                  />
                </TableCell>
                <TableCell>PO Details</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Progress</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No purchase orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => handleSelectOrder(order.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {order.poNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.items.length} items
                        </Typography>
                        {order.notes && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {order.notes.substring(0, 50)}...
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {order.supplierName || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {order.supplierGstin || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(order.orderDate)}
                      </Typography>
                      {order.expectedDelivery && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Expected: {formatDate(order.expectedDelivery)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(order.grandTotal)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ minWidth: 100 }}>
                        {order.status === PurchaseOrderStatus.ACKNOWLEDGED || 
                         order.status === PurchaseOrderStatus.PARTIAL_RECEIVED ? (
                          <Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={getReceiveProgress(order)} 
                              sx={{ mb: 0.5 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {getReceiveProgress(order).toFixed(0)}% received
                            </Typography>
                          </Box>
                        ) : (
                          <Stepper activeStep={getStatusStep(order.status)} alternativeLabel>
                            {statusSteps.slice(0, 4).map((step, index) => (
                              <Step key={step}>
                                <StepLabel />
                              </Step>
                            ))}
                          </Stepper>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={order.status.replace('_', ' ')}
                        color={getStatusColor(order.status)}
                        size="small"
                        icon={getStatusIcon(order.status)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, order.id)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedOrderId && handleViewOrder(selectedOrderId)}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        
        {selectedOrderId && hasPermission('purchase_orders.update') && canPerformAction(orders.find(o => o.id === selectedOrderId)!, 'edit') && (
          <MenuItem onClick={() => selectedOrderId && handleEditOrder(selectedOrderId)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit Order
          </MenuItem>
        )}

        {selectedOrderId && hasPermission('purchase_orders.update') && canPerformAction(orders.find(o => o.id === selectedOrderId)!, 'send') && (
          <MenuItem onClick={() => selectedOrderId && handleSendOrder(selectedOrderId)}>
            <SendIcon fontSize="small" sx={{ mr: 1 }} />
            Send to Supplier
          </MenuItem>
        )}

        {selectedOrderId && hasPermission('purchase_orders.update') && canPerformAction(orders.find(o => o.id === selectedOrderId)!, 'acknowledge') && (
          <MenuItem onClick={() => selectedOrderId && handleAcknowledgeOrder(selectedOrderId)}>
            <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
            Mark Acknowledged
          </MenuItem>
        )}

        {selectedOrderId && hasPermission('purchase_orders.receive') && canPerformAction(orders.find(o => o.id === selectedOrderId)!, 'receive') && (
          <MenuItem onClick={() => selectedOrderId && handleReceiveGoods(selectedOrderId)}>
            <LocalShippingIcon fontSize="small" sx={{ mr: 1 }} />
            Receive Goods
          </MenuItem>
        )}

        {selectedOrderId && hasPermission('purchase_bills.create') && canPerformAction(orders.find(o => o.id === selectedOrderId)!, 'convert') && (
          <MenuItem onClick={() => selectedOrderId && handleConvertToBill(selectedOrderId)}>
            <ReceiptIcon fontSize="small" sx={{ mr: 1 }} />
            Convert to Bill
          </MenuItem>
        )}

        <MenuItem onClick={() => selectedOrderId && handlePrintOrder(selectedOrderId)}>
          <PrintIcon fontSize="small" sx={{ mr: 1 }} />
          Print/Download
        </MenuItem>

        {selectedOrderId && hasPermission('purchase_orders.delete') && canPerformAction(orders.find(o => o.id === selectedOrderId)!, 'delete') && (
          <MenuItem
            onClick={() => {
              const order = orders.find(o => o.id === selectedOrderId);
              if (order) handleDeleteOrder(order);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete Order
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Purchase Order</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete purchase order "{orderToDelete?.poNumber}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteOrder} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Filter Purchase Orders</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as PurchaseOrderStatus || undefined }))}
                >
                  <MenuItem value="">All</MenuItem>
                  {Object.values(PurchaseOrderStatus).map(status => (
                    <MenuItem key={status} value={status}>
                      {status.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date From"
                type="date"
                value={filters.dateFrom || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date To"
                type="date"
                value={filters.dateTo || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => setFilterDialogOpen(false)} variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrderManagementMUI;